import React, { FC, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';
import { IconName, Loader, LogoContainer } from 'src/components';
import Link from 'next/link';
import {
  trackCommunicateTabOfficialClicked,
  trackHomeTabOfficialClicked,
  trackImmerseTabOfficialClicked,
  trackLearnTabOfficialClicked,
} from 'src/analytics';
import { useBrazeContext } from 'src/context/braze';
import { ExperienceType, useExperienceContext } from 'src/context/experience';
import { Appboy } from 'src/types/braze';
import { ROUTES } from 'src/constants/routes.const';
import * as S from './styles';

const msgs = defineMessages({
  home: { id: 'scenario_dashboard_sidebar_home_tab' },
  scenarios: { id: 'core_loop_tab_names_scenarios' },
  lessons: { id: 'learnq_lessons_tab_name' },
  videos: { id: 'core_loop_tab_names_video' },
  conversations: { id: 'core_loop_tab_names_conversations' },
  buddies: { id: 'ai_buddies_tab_title' },
  collapseSidebar: { id: 'scenario_dashboard_sidebar_collapse' },
  expandSidebar: { id: 'scenario_dashboard_sidebar_expand' },
});

// Hardcoded Avatar Tutor label
const avatarTutorLabel = { id: 'avatarTutor', defaultMessage: 'Avatar Tutor' };

interface Tab {
  label: {
    id: string;
    defaultMessage?: string;
  };
  icon: {
    default: IconName;
    active: IconName;
  };
  url: string;
  altUrls?: string[];
  tracking: () => void;
}

const getTabs: {
  (appboy?: Appboy, experienceType?: ExperienceType | null): Record<string, Tab>;
} = (appboy?: Appboy, experienceType?: ExperienceType | null) => ({
  home: {
    label: msgs.home,
    icon: {
      default: experienceType === '2025' ? 'logo' : 'homeOutline',
      active: experienceType === '2025' ? 'logo' : 'homeFilled',
    },
    url: ROUTES.dashboard,
    tracking: trackHomeTabOfficialClicked(appboy),
  },
  learn: {
    label: experienceType === '2025' ? msgs.lessons : msgs.scenarios,
    icon: {
      default: experienceType === '2025' ? 'homeOutline' : 'learnOutline',
      active: experienceType === '2025' ? 'homeFilled' : 'learnFilled',
    },
    url: experienceType === '2025' ? ROUTES.myLessons : ROUTES.scenarios.learn,
    altUrls: [ROUTES.scenarios.practice],
    tracking: trackLearnTabOfficialClicked(appboy),
  },
  immerse: {
    label: msgs.videos,
    icon: {
      default: 'videoLibraryOutline',
      active: 'videoLibraryFilled',
    },
    url: ROUTES.video.immerse,
    tracking: trackImmerseTabOfficialClicked(appboy),
  },
  communicate: {
    label: msgs.conversations,
    icon: {
      default: 'communicateOutline',
      active: 'communicateFilled',
    },
    url: ROUTES.membot.select,
    tracking: trackCommunicateTabOfficialClicked(appboy),
  },
  buddies: {
    label: msgs.buddies,
    icon: {
      default: 'glassesOutline',
      active: 'glassesFilled',
    },
    url: ROUTES.ai.buddies,
    // eslint-disable-next-line no-warning-comments
    tracking: () => {}, // TODO: add analytics
  },
  avatarTutor: {
    label: avatarTutorLabel,
    icon: {
      default: 'microphoneOutline',
      active: 'microphoneFilled',
    },
    url: ROUTES.avatarTutor,
    // eslint-disable-next-line no-warning-comments
    tracking: () => {}, // TODO: add analytics
  },
});

const mobileNavbarTabs = ['home', 'learn', 'immerse', 'communicate'];
const sidebarTabs = ['home', 'learn', 'immerse', 'communicate'];

interface BaseProps {
  url?: string;
  isLoading?: boolean;
  isSupportedLanguagePairBuddies?: boolean;
  isSupportedLanguagePairAvatarTutor?: boolean;
}

interface Props extends BaseProps {
  showContent: boolean;
}

export const MobileNavBar: FC<BaseProps> = ({ url, isLoading, isSupportedLanguagePairBuddies }) => {
  const intl = useIntl();
  const { appboy } = useBrazeContext();
  const { experienceType } = useExperienceContext();

  const tabs = useMemo(() => {
    const tabList = [...mobileNavbarTabs];
    if (isSupportedLanguagePairBuddies) tabList.push('buddies');
    // if (isSupportedLanguagePairAvatarTutor) tabList.push('avatarTutor');
    return tabList;
  }, [isSupportedLanguagePairBuddies]);

  return (
    <S.MobileNavBarWrapper>
      {isLoading ? (
        <S.LoaderContainer>
          <Loader />
        </S.LoaderContainer>
      ) : (
        tabs.map(tabName => {
          const tabData = getTabs(appboy, experienceType)[tabName];
          const { url: tabUrl, altUrls = [] } = tabData;
          const isActive = url !== undefined && [tabUrl, ...altUrls].includes(url);

          return (
            <Link key={`tab-${tabName}`} href={tabUrl} passHref legacyBehavior>
              <S.MobileSidebarLink
                data-testid={`tab-${tabName}`}
                $active={isActive}
                onClick={tabData.tracking}
              >
                <S.IconMobile name={tabData.icon[isActive ? 'active' : 'default']} />
                <S.TextMobile>{intl.formatMessage(tabData.label)}</S.TextMobile>
              </S.MobileSidebarLink>
            </Link>
          );
        })
      )}
    </S.MobileNavBarWrapper>
  );
};

export const Sidebar: FC<Props> = ({
  url,
  showContent,
  isLoading,
  isSupportedLanguagePairBuddies,
}) => {
  const intl = useIntl();
  const { appboy } = useBrazeContext();
  const { experienceType } = useExperienceContext();

  const tabs = useMemo(() => {
    const tabList = [...sidebarTabs];
    if (isSupportedLanguagePairBuddies) tabList.push('buddies');
    // if (isSupportedLanguagePairAvatarTutor) tabList.push('avatarTutor');
    return tabList;
  }, [isSupportedLanguagePairBuddies]);

  const previouslyCollapsed = getLocalStorageEntry('scenarioSidebarIsCollapsed') === 'true';
  const [isCollapsed, setIsCollapsed] = useState(previouslyCollapsed || false);

  const onClick = () => {
    const isNowCollapsed = !isCollapsed;
    setLocalStorageEntry('scenarioSidebarIsCollapsed', isNowCollapsed.toString());
    setIsCollapsed(isNowCollapsed);
  };

  const sidebarText = isCollapsed
    ? intl.formatMessage(msgs.expandSidebar)
    : intl.formatMessage(msgs.collapseSidebar);

  const getSidebarTab = (tabName: string) => {
    const tabData = getTabs(appboy, experienceType)[tabName];
    const { url: tabUrl, altUrls = [] } = tabData;
    const isActive = url !== undefined && [tabUrl, ...altUrls].includes(url);

    return (
      <S.SidebarButtonWrapper
        key={`tab-${tabName}`}
        $active={isActive}
        data-testid={`tab-${tabName}`}
      >
        <Link href={tabUrl} passHref legacyBehavior>
          <S.SidebarLink
            $active={isActive}
            data-testid={`tab-${tabName}-link`}
            onClick={tabData.tracking}
          >
            <S.Icon name={tabData.icon[isActive ? 'active' : 'default']} $collapsed={isCollapsed} />
            {!isCollapsed && intl.formatMessage(tabData.label)}
          </S.SidebarLink>
        </Link>
        {isActive && <S.ActiveMarker />}
      </S.SidebarButtonWrapper>
    );
  };

  return (
    <S.SidebarWrapper>
      {showContent && (
        <S.SidebarCollapseButton
          onClick={onClick}
          $collapsed={isCollapsed}
          data-testid="sidebarCollapseButton"
          title={sidebarText}
          aria-label={sidebarText}
        >
          <S.CollapseIcon name={isCollapsed ? 'chevronRight' : 'chevronLeft'} />
        </S.SidebarCollapseButton>
      )}
      <S.Sidebar $collapsed={isCollapsed || !showContent}>
        <S.HeaderSection>
          <LogoContainer
            url={ROUTES.dashboard}
            isMobileVisible={false}
            fitContent
            iconColor="primary20"
            largerIcon
          />
        </S.HeaderSection>
        {isLoading ? (
          <S.LoaderContainer>
            <Loader />
          </S.LoaderContainer>
        ) : (
          <>{showContent && tabs.map((tabName: string) => getSidebarTab(tabName))}</>
        )}
      </S.Sidebar>
    </S.SidebarWrapper>
  );
};
